import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';

import graph from './graph';
import { useSelector } from 'react-redux';
import { isEmptyObject } from 'helpers/formatObject';

import { addCommas, digitsEnToFa } from '@persian-tools/persian-tools';
import { Avatar, Card, CardContent, CardHeader, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { CircularProgress } from '@mui/material';

import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';

import jMoment from 'moment-jalaali';
import { CountdownTimer } from 'components';
jMoment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });

const type = {
  PURCHASE: 'پرداخت آنلاین',
  REFUND_ORDER: 'بازگشت هزینه سفارش',
  REFUND_ORDER_DETAIL: 'بازگشت هزینه محصول',
  REFUND_SHIPPMENT: 'بازگشت هزینه حمل',
  SETTLEMENT: 'تسویه حساب',
  REFOUND_EXTRA: 'بازگشت هزینه دستی',
  DEPOSIT: 'پرداخت نقدی',
  COUPON: 'پرداخت با بن',
};

const status = {
  FAILED: 'ناموفق',
  PENDING: 'در انتظار',
  SUCCEED: 'موفقیت آمیز',
};

const statusAvatar = {
  FAILED: (
    <Avatar sx={{ bgcolor: 'error.lighter' }}>
      <HighlightOffRoundedIcon color="error" fontSize="small" />
    </Avatar>
  ),
  PENDING: (
    <Avatar sx={{ bgcolor: 'warning.lighter' }}>
      <AccessTimeRoundedIcon color="warning" fontSize="small" />
    </Avatar>
  ),
  SUCCEED: (
    <Avatar sx={{ bgcolor: 'success.lighter' }}>
      <CheckCircleOutlineRoundedIcon color="success" fontSize="small" />
    </Avatar>
  ),
};

export default function Content({ model }) {
  const {
    id,
    call_me,
    fast_send,
    total_cost,
    created_at,
    demand_time,
    delivery_time,
    tracking_code,
    shop,
    status,
    customer,
  } = model;
  const [result, setResult] = useState({});
  const { userToken } = useSelector((state) => state.auth);

  const [getData, { loading }] = useLazyQuery(graph.get.query, {
    variables: {
      ids: id,
      for_admin: 1,
    },
    context: {
      serviceName: graph.get.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const fetchData = async () => {
    try {
      const { data } = await getData();
      if (!isEmptyObject(data)) {
        const res = data[graph.get.name]?.data[0];
        setResult(res);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100%" rowGap={2}>
        <CircularProgress size={26} />
        <Typography variant="caption">در حال بارگذاری...</Typography>
      </Stack>
    );
  }

  return (
    <Stack justifyContent="space-between" height="100%" rowGap={1}>
      <Stack rowGap={2} p={2} bgcolor="#fff" borderRadius={2}>
        <Price divider title="هزینه محصولات (قیمت کارخانه)" amount={result?.co_products_cost} />
        <Price divider title="هزینه محصولات (قیمت فروشگاه)" amount={result?.me_products_cost} />
        <Price divider title="تخفیف محصولات" amount={result?.products_discount} />

        {result?.order_discount && <Price title={result?.discount_title} amount={result?.order_discount} />}
        {result.coupons && result?.coupons.length > 0 && (
          <Stack rowGap={1}>
            {result.coupons.map((coupon, i) => (
              <Price key={i} title={`بن ${coupon?.coupon?.title}`} amount={coupon?.amount} tiny />
            ))}
          </Stack>
        )}

        <Price divider title="کل تخفیف" amount={result?.total_discount} />
        <Price divider title="هزینه حمل" amount={result?.shipping_cost} />
        <Price divider title="مبلغ تسویه شده از کیف پول" amount={result?.wallet_settled_amount} />
        <Price divider title="مبلغ پرداخت شده" amount={result?.paid_cost} />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="primary.main"
        color="#fff"
        p={2}
        borderRadius={2}
      >
        <Typography variant="h5">قابل پرداخت</Typography>
        <Typography variant="h3">
          {digitsEnToFa(addCommas(Number(result?.total_cost)))}
          <Typography mx={1} fontSize={10} variant="subtitle" component="span">
            تومان
          </Typography>
        </Typography>
      </Stack>

      <Card>
        <CardHeader title="تراکنش ها" />
        <CardContent sx={{ px: 1 }}>
          {result.transactions &&
            result.transactions.map((row, i) => (
              <Tooltip key={i} title={status[row.status]}>
                <Card variant="outlined">
                  <CardHeader
                    title={type[row.type]}
                    subheader={
                      <Stack alignItems="flex-start">
                        {row.ref_code && <Chip size="small" label={digitsEnToFa(row.ref_code || '')} />}
                        {row.detail?.pan && (
                          <Chip
                            sx={{ fontSize: 12 }}
                            size="small"
                            label={`شماره کارت ${digitsEnToFa(row.detail?.pan || '')}`}
                          />
                        )}
                      </Stack>
                    }
                    avatar={statusAvatar[row.status]}
                    action={
                      <Stack justifyContent="center" alignItems="flex-end" columnGap={0.5}>
                        {/* <Paid total={row.price} terminal={row.detail?.terminal?.title} /> */}
                        
                        <Price divider amount={row.price} />
                        <Stack direction="row" columnGap={0.5}>
                          <Typography fontSize={14} color='text.disabled'>
                            {digitsEnToFa(jMoment(row.created_at).format('jD jMMMM jYYYY'))}
                          </Typography>
                          <Typography fontSize={14} color='text.disabled'>
                            ساعت {digitsEnToFa(jMoment(row.created_at).format('hh:mm'))}
                          </Typography>
                        </Stack>
                      </Stack>
                    }
                  />
                </Card>
              </Tooltip>
            ))}
        </CardContent>
      </Card>
    </Stack>
  );
}

const Price = ({ amount, title, tiny, divider }) => {
  return (
    amount > 0 && (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography ml={tiny && 2} fontSize={tiny ? 14 : 18} color="text.secondary" variant="body1">
            {title}
          </Typography>
          <Typography color="text.secondary" fontSize={tiny ? 18 : 22} variant="subtitle1">
            {digitsEnToFa(addCommas(Number(amount)))}
            <Typography ml={1} fontSize={10} variant="subtitle1" component="span">
              تومان
            </Typography>
          </Typography>
        </Stack>
      </>
    )
  );
};

const Paid = ({ total, terminal }) => (
  <Stack
    alignItems="center"
    direction="row"
    columnGap={1}
    height={48}
    width={140}
    bgcolor="error.lighter"
    sx={{ borderRadius: 2, overflow: 'hidden', px: 1 }}
  >
    <Avatar sx={{ bgcolor: 'error.lighter' }}>
      <PaidRoundedIcon fontSize="small" color="error" />
    </Avatar>
    <Stack flex={1} alignItems="center">
      <Typography fontWeight="bold" fontSize={16} variant="subtitle1">
        {digitsEnToFa(addCommas(Number(total)))}
      </Typography>
      {terminal && (
        <Typography fontWeight="bold" fontSize={12} variant="subtitle1">
          {terminal}
        </Typography>
      )}
    </Stack>
  </Stack>
);
